<template>
  <div class="relative contain-paint min-h-[100px]" :class="containerClasses">
    <Card.Base class="z-2" :class="cardClasses">
      <Card.Title :class="titleClasses">
        <img class="w-[65px]" :src="DacrAILogo" />

        <div
          v-if="showBadge"
          class="text-[12px] leading-[15px] bg-gradient-to-r from-[#30BAC6]/20 from-60% to-[#69B393]/20 absolute top-0 right-0 rounded-tr-[10px] rounded-bl-xl h-7 text-gray-900 flex items-center px-3"
        >
          <img :src="Fingerprint" class="w-[14px] mr-2" />
          Registered for protection
        </div>
      </Card.Title>

      <Card.Content>
        <!-- Public access restriction -->
        <div v-if="isPublic" class="flex flex-col items-center mb-[22px]">
          <Pill class="mb-2">
            Only owners and authors can view this information
          </Pill>
          <Link>Learn more about Dacr AI</Link>
        </div>

        <!-- Processing state -->
        <div
          v-else-if="isProcessing"
          class="flex justify-around mb-[22px] text-gray-500"
        >
          Creating technical analysis
        </div>

        <!-- Complete analysis -->
        <template v-else-if="analysis">
          <!-- Genetic file markers -->
          <div class="mb-[22px]">
            <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
              Genetic file markers
            </div>
            <Markers :markers="markers" />
          </div>

          <Card.Separator />

          <!-- Video frames filmstrip -->
          <div
            v-if="videoFrames.length"
            ref="framesContainer"
            class="flex h-9 overflow-hidden mb-[22px] gap-px"
          >
            <!-- Hidden measurement image -->
            <img
              v-if="!framesReady"
              :src="videoFrames[0]"
              class="h-9 invisible absolute"
              @load="onFrameLoad"
            />

            <template
              v-for="(frameUrl, index) in distributedFrames"
              :key="index"
            >
              <div
                class="relative h-9 shrink-0"
                :style="{ width: frameWidth + 'px' }"
              >
                <Skeleton class="absolute inset-0" />
                <img
                  :src="frameUrl"
                  class="absolute inset-0 h-full w-full opacity-0 transition-opacity duration-300"
                  @load="
                    ($event.target as HTMLImageElement).classList.replace(
                      'opacity-0',
                      'opacity-100'
                    )
                  "
                />
              </div>
            </template>

            <!-- Skeleton placeholders before measurement -->
            <template v-if="!framesReady">
              <Skeleton
                v-for="n in 9"
                :key="'sk-' + n"
                class="h-9 w-16 shrink-0"
              />
            </template>
          </div>

          <!-- Summary -->
          <div>
            <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
              Summary
            </div>
            <div class="leading-[20px]">
              {{ analysis.summary }}
            </div>
          </div>

          <Card.Separator />

          <!-- Attributes -->
          <Attributes :attributes="analysis.attributes" />

          <!-- Transcript + tracks/stems (audio & video) -->
          <template v-if="transcriptPreview.length">
            <Card.Separator />

            <Transcript :entries="transcriptPreview" />
          </template>

          <!-- Single track: show stems inline -->
          <template v-if="singleTrack">
            <Card.Separator />

            <div>
              <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
                Stems ({{ singleTrack.stems.breakdown.length }})
              </div>

              <div class="flex flex-col gap-y-[14px]">
                <Stem
                  v-for="stem in singleTrack.stems.breakdown"
                  :key="stem.name"
                  :name="stem.name"
                  :url="stem.url"
                  :sheet-music="stem.sheetMusic?.preview"
                  :track-index="0"
                />
              </div>
            </div>
          </template>

          <!-- Multiple tracks: show track cards -->
          <template v-else-if="tracks.length > 1">
            <Card.Separator />

            <div>
              <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
                Audio tracks found
              </div>

              <div class="flex flex-col gap-y-2.5">
                <TrackCard
                  v-for="(track, trackIndex) in tracks"
                  :key="trackIndex"
                  :track="track"
                  :index="trackIndex"
                />
              </div>
            </div>
          </template>
        </template>
      </Card.Content>
    </Card.Base>

    <div v-if="isProcessing" class="border--snake z-0"></div>
  </div>
</template>

<script setup lang="ts">
import { Card, Pill, Skeleton } from '@dacr/ui'
import Attributes from '@/shared/components/Attributes.vue'
import Transcript from '@/features/copyright/components/Transcript.vue'
import Stem from '@/features/copyright/components/Stem.vue'
import Markers from '@/features/copyright/components/Markers.vue'
import Link from '@/shared/components/Link.vue'
import TrackCard from './TrackCard.vue'
import { FileRole } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import type { Marker } from '@/shared/graphql/legacy/generated'
import { computed, ref, watch } from 'vue'
import Fingerprint from '@/assets/images/Fingerprint.png'
import DacrAILogo from '@/assets/images/DacrAI.png'

type FileAnalysis = NonNullable<CopyrightFile['analysis']>
type VideoFileAnalysis = Extract<FileAnalysis, { __typename?: 'VideoAnalysis' }>
type AudioFileAnalysis = Extract<FileAnalysis, { __typename?: 'AudioAnalysis' }>

const props = defineProps<{
  file?: CopyrightFile
}>()

const analysis = computed(() => props.file?.analysis)

const isPublic = computed(() => props.file?.role === FileRole.Public)
const isProcessing = computed(() => !analysis.value && !isPublic.value)

const showBadge = computed(() => isPublic.value || !isProcessing.value)

const videoAnalysis = computed<VideoFileAnalysis | null>(() => {
  if (analysis.value?.__typename === 'VideoAnalysis') {
    return analysis.value as VideoFileAnalysis
  }
  return null
})

const audioAnalysis = computed<AudioFileAnalysis | null>(() => {
  if (analysis.value?.__typename === 'AudioAnalysis') {
    return analysis.value as AudioFileAnalysis
  }
  return null
})

const tracks = computed(
  () => videoAnalysis.value?.tracks ?? audioAnalysis.value?.tracks ?? []
)

const transcriptPreview = computed(
  () =>
    videoAnalysis.value?.transcript?.preview ??
    audioAnalysis.value?.transcript?.preview ??
    []
)

const singleTrack = computed(() =>
  tracks.value.length === 1 ? tracks.value[0] : null
)

const videoFrames = computed(() => videoAnalysis.value?.frames ?? [])

// Filmstrip: show first + last frame with evenly distributed frames in between
const framesContainer = ref<HTMLElement>()
const containerWidth = ref(0)
const frameWidth = ref(0)

const FRAME_GAP = 1 // gap-px

const onFrameLoad = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    frameWidth.value = Math.round((36 / img.naturalHeight) * img.naturalWidth)
  }
}

const framesReady = computed(
  () => containerWidth.value > 0 && frameWidth.value > 0
)

watch(framesContainer, (el, _, onCleanup) => {
  if (!el) {
    containerWidth.value = 0
    return
  }
  const observer = new ResizeObserver(([entry]) => {
    containerWidth.value = entry.contentRect.width
  })
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})

const distributedFrames = computed(() => {
  if (!framesReady.value) return []
  const frames = videoFrames.value
  if (frames.length <= 2) return frames

  const maxFrames = Math.max(
    2,
    Math.ceil(
      (containerWidth.value - frameWidth.value) /
        (frameWidth.value + FRAME_GAP) +
        1
    )
  )

  if (frames.length <= maxFrames) return frames

  const result: string[] = [frames[0]]
  const innerCount = maxFrames - 2
  for (let i = 1; i <= innerCount; i++) {
    const index = Math.round((i * (frames.length - 1)) / (maxFrames - 1))
    result.push(frames[index])
  }
  result.push(frames[frames.length - 1])

  return result
})

const markers = computed(
  () => (analysis.value?.markers ?? []) as (Marker | null)[]
)

const containerClasses = computed(() => ({
  'rounded-[14px]': isProcessing.value,
  'rounded-xl': !isProcessing.value,
}))

const cardClasses = computed(() => ({
  'border-2 border-transparent border--glow':
    !isProcessing.value && !isPublic.value,
  'absolute border-none outline outline-[#CCC]/50 inset-0.5 w-auto!':
    isProcessing.value,
}))

const titleClasses = computed(() => ({
  'border-none pb-0!': isPublic.value || isProcessing.value,
  'mb-1.5!': isProcessing.value,
}))
</script>

<style scoped>
.border--snake {
  background: conic-gradient(
    rgb(27, 190, 219, 0) 10deg,
    rgb(27, 190, 219, 0.5) 10deg,
    rgba(27, 190, 219, 1) 38.9738deg,

    rgba(219, 49, 153, 1) 50deg,
    rgba(219, 49, 153, 0.5) 62deg,
    rgba(219, 49, 153, 1) 97deg,

    rgba(248, 70, 0, 1) 107deg,
    rgba(248, 70, 0, 0.5) 150deg,

    rgba(255, 162, 11, 1) 180deg,
    rgba(255, 162, 11, 0) 200deg,

    rgba(27, 190, 219, 0) 360deg
  );
  position: absolute;
  width: 100%;
  height: 200px;
  inset: 0;
  scale: 5 2;
  translate: 0 -80px;
  z-index: 1;
  animation: ae-r 4s linear infinite;
}

@keyframes ae-r {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
